import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Icon } from '../shared/components/Icon';
import { getActiveLocale } from '../shared/i18n/i18n';
import { currencyLabel, formatDate, formatNumber, formatRelativeTime } from '../shared/lib/formatters';
import { type QueuedWrite, useOffline } from './OfflineProvider';
import type { OutboxState } from './outbox';
import type { SyncPause } from './sync';

/**
 * The one place that answers "is my work safe?".
 *
 * It is a status chip in the top bar and a panel behind it, and it only appears when it has
 * something to say: the connection is gone, writes are waiting, something needs a decision, or
 * this device holds an offline copy at all. A workspace that is simply online with nothing
 * pending gets no chip, because there is nothing to reassure anyone about.
 */

const stateTone: Readonly<Record<OutboxState, string>> = {
  pending: 'pending',
  failed: 'pending',
  rejected: 'blocked',
  uncertain: 'blocked',
};

function pauseKey(pausedBy: SyncPause): string {
  return `offline.pause.${pausedBy}`;
}

function QueuedItem({ item }: { item: QueuedWrite }) {
  const { t } = useTranslation();
  const locale = getActiveLocale();
  const { discard, retry } = useOffline();
  const [busy, setBusy] = useState(false);
  const { record, detail } = item;
  const needsDecision = record.state === 'rejected' || record.state === 'uncertain';

  const act = async (action: (id: string) => Promise<void>) => {
    setBusy(true);
    try {
      await action(record.id);
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className={`queue-item queue-item--${stateTone[record.state]}`}>
      <div className="queue-item__copy">
        <strong>{t(record.labelKey)}</strong>
        {detail !== null && (
          <small dir="auto">
            {detail.postingDate !== undefined && formatDate(detail.postingDate, locale)}
            {detail.lineCount !== undefined && <> · {t('offline.queue.lines', { count: detail.lineCount })}</>}
            {detail.amount !== undefined && detail.amount > 0 && (
              <> · <bdi>{formatNumber(detail.amount, locale)} {currencyLabel(detail.currencyCode ?? '')}</bdi></>
            )}
          </small>
        )}
        {detail?.description !== undefined && detail.description !== '' && (
          <small className="queue-item__description" dir="auto">{detail.description}</small>
        )}
        <small className="queue-item__meta">
          {formatRelativeTime(record.createdAt, locale)}
          {record.lastError !== null && (
            <> · {t(`offline.error.${record.lastError.code ?? 'unknown'}`, { defaultValue: t('offline.error.unknown') })}</>
          )}
        </small>
      </div>

      <div className="queue-item__side">
        <span className={`queue-state queue-state--${stateTone[record.state]}`}>{t(`offline.state.${record.state}`)}</span>
        {needsDecision && (
          <span className="queue-item__actions">
            <button type="button" className="text-link" onClick={() => void act(retry)} disabled={busy}>
              {t('common.retry')}
            </button>
            <button type="button" className="text-link text-link--danger" onClick={() => void act(discard)} disabled={busy}>
              {t('offline.discard')}
            </button>
          </span>
        )}
      </div>
    </li>
  );
}

export function SyncCentre() {
  const { t } = useTranslation();
  const locale = getActiveLocale();
  const offline = useOffline();
  const { counts, enabled, online, queue, syncing, syncState, supported, vault } = offline;

  if (!supported) return null;
  if (online && counts.total === 0 && !enabled) return null;

  const tone = !online
    ? 'offline'
    : counts.needsAttention > 0
      ? 'attention'
      : counts.unsent > 0
        ? 'pending'
        : 'clear';

  const chipLabel = !online
    ? t('offline.chip.offline')
    : counts.unsent > 0
      ? t('offline.chip.waiting', { count: counts.unsent })
      : counts.needsAttention > 0
        ? t('offline.chip.attention', { count: counts.needsAttention })
        : t('offline.chip.synced');

  return (
    <details className={`popover sync-popover sync-popover--${tone}`}>
      <summary className="sync-chip" aria-label={t('offline.chip.label')}>
        <Icon name={online ? (counts.unsent > 0 ? 'sync' : 'cloud') : 'cloudOff'} className={`icon${syncing ? ' icon--spinning' : ''}`} />
        <span className="sync-chip__copy">{chipLabel}</span>
        {counts.total > 0 && <span className="sync-chip__count">{formatNumber(counts.total, locale)}</span>}
      </summary>

      <div className="popover__panel popover__panel--end sync-panel">
        <div className="sync-panel__state">
          <strong>{online ? t('offline.status.online') : t('offline.status.offline')}</strong>
          <small>
            {!enabled
              ? t('offline.status.notEnabled')
              : !offline.unlocked
                ? t('offline.status.sealed')
                : vault !== null
                  ? t('offline.status.copyConfirmed', { when: formatRelativeTime(vault.lastOnlineAt, locale) })
                  : ''}
          </small>
        </div>

        {syncState.lastRunAt !== null && (
          <p className="sync-panel__last">
            {t('offline.lastSync', { when: formatRelativeTime(syncState.lastRunAt, locale) })}
            {syncState.lastResult?.pausedBy != null && <> · {t(pauseKey(syncState.lastResult.pausedBy))}</>}
          </p>
        )}

        {queue.length > 0 ? (
          <ul className="queue-list">
            {queue.map((item) => <QueuedItem key={item.record.id} item={item} />)}
          </ul>
        ) : (
          <p className="sync-panel__empty">{t('offline.queue.empty')}</p>
        )}

        <div className="sync-panel__actions">
          <button
            className="button button--secondary"
            type="button"
            onClick={() => void offline.sync()}
            disabled={syncing || !online || counts.unsent === 0}
          >
            <Icon name="sync" />
            {syncing ? t('offline.syncing') : t('offline.syncNow')}
          </button>
          <Link className="text-link" to="/console/settings?device=1">{t('offline.manageDevice')}</Link>
        </div>
      </div>
    </details>
  );
}
